import { IndustrySlug } from 'containers/Setting/components/RoleFormModal/customerIntelligence';

import { isIndustrySlug } from './industryCards';

export interface ProductReadinessOption {
  // Stable snake_case identifier: it travels into the card data seam's cache key, so renaming one
  // silently orphans a tenant's selection. PLA-8815.
  id: string;
  // FR § Product Readiness per industry, verbatim. Product names arrive with the payload and are
  // never translated, so these labels stay out of i18n.
  label: string;
  /**
   * The noun the two holding branches are named after — "No {noun}" / "Existing {noun} Holder".
   * Set only where the product's own label does not read as that noun: `label` is an offer
   * ("Credit Card / Limit Upgrade") and `4182-9637` names the populations after the thing HELD
   * ("No Credit Card" / "Existing Card Holder"). Every other product's label already is the noun, so
   * it falls back to `label` rather than carrying an invented per-product word — the per-industry
   * branch wording is the Metric List's to state. PLA-8815.
   */
  holdingNoun?: string;
  /**
   * How this product answers "does the customer already hold it". Optional: a product without one
   * falls back to the catalogue's generic `target_product_holding_status` field, which is the whole
   * point of the fallback — every product in the catalogue has the same two branches, so adding the
   * next one costs a line here and nothing in a layout. PLA-8828.
   */
  holding?: ProductHoldingRule;
  /**
   * The `product_family` values this catalogue option is sold as on the wire.
   *
   * The reason it exists: one `O-*-PR-*` question answers with EVERY product's cells, not just the
   * selected one — all nine `O-FIN-PR-*` return the same 434-row table covering nine offers — so the
   * card has to filter, and the catalogue id is not what the rows are keyed by. Matched on
   * `product_family` and not on `product_offer_id`, which is a per-tenant hash
   * (`offer_6a7282be4c6ec54b`) and so cannot be written down here.
   *
   * Populated only where a live response has NAMED the families: fintech (`api-uat.perxtech.io`
   * 2026-09-09) and banking (`C-BFS-PR-*` / `O-BFS-PR-*`, 2026-10-08, eleven families). Insurance and
   * telco still return zero rows, so inventing their slugs would be a guess the card would then
   * silently filter on. PLA-8815, PLA-8828.
   */
  wireProductFamilies?: string[];
}

/**
 * The two branches every product in the catalogue has, plus the state before the field has answered.
 *
 * `unknown` is NOT `not_held`: the catalogue's rule turns on a CONFIRMED non-holder, and treating a
 * missing answer as a confirmed "No" would suppress the limit, utilisation and repayment fields for
 * a customer nobody has checked. PLA-8828.
 */
export type ProductHoldingStatus = 'held' | 'not_held' | 'unknown';

/**
 * The catalogue's generic holding field, read for any product that names no field of its own.
 * `credit_card_holding` is the per-product override the catalogue spells out for Credit Card; the
 * other products' own field names are the Metric List's to state, so none is invented here.
 */
export const TARGET_PRODUCT_HOLDING_STATUS_KEY = 'target_product_holding_status';

export interface ProductHoldingRule {
  /** The per-product holding field, where the catalogue names one. */
  statusKey?: string;
  /**
   * What the readiness reading MEANS in each branch. The catalogue states this for Credit Card:
   * `No` reads as Credit Card Acquisition Readiness, `Yes` as Credit Limit Upgrade Readiness — the
   * same score under two different questions, which is why the branch has to reach the label.
   */
  readinessLabel?: Record<'held' | 'not_held', string>;
  /**
   * Metric keys that are NOT APPLICABLE for a confirmed non-holder — the card-specific limit,
   * utilisation and repayment fields. They must not be defaulted to zero and must not be reported as
   * insufficient data: neither is true, the question simply does not arise.
   */
  notApplicableWhenNotHeld?: string[];
}

export interface IndustryProductCatalogue {
  options: ProductReadinessOption[];
  recommendedDefaultId: string;
}

// AF-5 is open — the FR's card-permission table also demands the industry parent, while the frozen
// PLA-8807 catalog excludes `product_level_readiness` from `INDUSTRY_KEYS`, so the shipped descriptor
// in `registry/cards.ts` stays `industryScoped: false`. The option list below is industry-scoped
// either way: it answers "which products exist for this tenant", not "may this role see the card".
// PLA-8815 AF-5.
export const PRODUCT_READINESS_CATALOGUE: Record<IndustrySlug, IndustryProductCatalogue> = {
  banking_bfsi: {
    recommendedDefaultId: 'credit_card_limit_upgrade',
    options: [
      {
        id: 'credit_card_limit_upgrade',
        label: 'Credit Card / Limit Upgrade',
        holdingNoun: 'Credit Card',
        wireProductFamilies: ['credit_card'],
        holding: {
          statusKey: 'credit_card_holding',
          readinessLabel: {
            not_held: 'Credit Card Acquisition Readiness',
            held: 'Credit Limit Upgrade Readiness'
          },
          // The three card-specific field families 3583-15941 drops entirely against 3583-10956.
          notApplicableWhenNotHeld: [
            'credit_limit',
            'credit_limit_utilisation',
            'repayment_history'
          ]
        }
      },
      { id: 'personal_loan', label: 'Personal Loan', wireProductFamilies: ['personal_loan'] },
      {
        id: 'mortgage_home_loan',
        label: 'Mortgage / Home Loan',
        wireProductFamilies: ['mortgage']
      },
      { id: 'auto_secured_loan', label: 'Auto / Secured Loan', wireProductFamilies: ['auto_loan'] },
      {
        id: 'overdraft_credit_line',
        label: 'Overdraft / Credit Line',
        wireProductFamilies: ['overdraft']
      },
      {
        id: 'savings_account_deposit_product',
        label: 'Savings Account / Deposit Product',
        wireProductFamilies: ['savings']
      },
      { id: 'term_deposit', label: 'Term Deposit', wireProductFamilies: ['term_deposit'] },
      {
        id: 'investment_wealth_product',
        label: 'Investment / Wealth Product',
        wireProductFamilies: ['investment']
      },
      {
        id: 'insurance_cross_sell',
        label: 'Insurance Cross-sell',
        wireProductFamilies: ['bancassurance_referral']
      }
    ]
  },
  /* The nine `product_family` slugs below are the ones `O-FIN-PR-*` actually returned on
     `api-uat.perxtech.io` 2026-09-09 (tenant `generic`, industry `fintech_wallet_payment`). Two
     options take TWO families because the wire sells as two offers what the FR names as one product,
     and both slashed labels say so: a linked card is issued physical and virtual, and savings and
     investment are separate pockets. Their readings are therefore a sum ACROSS two offers, which is
     an upper bound on a customer union rather than a distinct count — flagged for product rather
     than resolved here, because dropping either offer would hide half the product instead.
     `wallet_top_up` and `p2p_p2m_qr_adoption` are named by the FR and by NO wire family, so they
     carry none and read as "nothing configured for this product". PLA-8815. */
  fintech_wallet_payment: {
    recommendedDefaultId: 'wallet_activation_reactivation',
    options: [
      {
        id: 'wallet_activation_reactivation',
        label: 'Wallet Activation / Reactivation',
        wireProductFamilies: ['wallet_core']
      },
      { id: 'wallet_top_up', label: 'Wallet Top-up' },
      {
        id: 'bill_payment_adoption',
        label: 'Bill Payment Adoption',
        wireProductFamilies: ['bill_pay']
      },
      { id: 'p2p_p2m_qr_adoption', label: 'P2P / P2M / QR Adoption' },
      {
        id: 'wallet_linked_card',
        label: 'Wallet-linked Card',
        wireProductFamilies: ['linked_card_physical', 'linked_card_virtual']
      },
      { id: 'bnpl_credit', label: 'BNPL / Credit', wireProductFamilies: ['bnpl'] },
      {
        id: 'savings_investment_feature',
        label: 'Savings / Investment Feature',
        wireProductFamilies: ['savings_pocket', 'invest_feature']
      },
      {
        id: 'insurance_protection_feature',
        label: 'Insurance / Protection Feature',
        wireProductFamilies: ['insurance_feature']
      },
      {
        id: 'merchant_business_services',
        label: 'Merchant / Business Services',
        wireProductFamilies: ['merchant_services']
      }
    ]
  },
  insurance: {
    recommendedDefaultId: 'policy_renewal',
    options: [
      { id: 'policy_renewal', label: 'Policy Renewal' },
      { id: 'coverage_top_up', label: 'Coverage Top-up' },
      { id: 'rider_add_on', label: 'Rider Add-on' },
      { id: 'life_insurance_cross_sell', label: 'Life Insurance Cross-sell' },
      { id: 'health_insurance_cross_sell', label: 'Health Insurance Cross-sell' },
      { id: 'motor_insurance_cross_sell', label: 'Motor Insurance Cross-sell' },
      { id: 'travel_insurance', label: 'Travel Insurance' },
      { id: 'home_property_insurance', label: 'Home / Property Insurance' },
      { id: 'premium_payment_plan', label: 'Premium Payment Plan' },
      { id: 'lapse_prevention_service_recovery', label: 'Lapse Prevention / Service Recovery' }
    ]
  },
  telco: {
    recommendedDefaultId: 'mobile_plan_upgrade',
    options: [
      { id: 'mobile_plan_upgrade', label: 'Mobile Plan Upgrade' },
      { id: 'data_pack_add_on', label: 'Data Pack / Add-on' },
      { id: 'roaming_pack', label: 'Roaming Pack' },
      { id: 'device_upgrade_financing', label: 'Device Upgrade / Financing' },
      { id: 'family_multi_line_plan', label: 'Family / Multi-line Plan' },
      { id: 'broadband_fibre', label: 'Broadband / Fibre' },
      { id: 'content_ott_bundle', label: 'Content / OTT Bundle' },
      { id: 'retention_renewal_offer', label: 'Retention / Renewal Offer' }
    ]
  }
};

const NO_OPTIONS: ProductReadinessOption[] = [];

// `isIndustrySlug` (the shipped `hasOwnProperty.call` guard) rather than `in`: `in` walks the
// prototype chain, so a tenant industry of 'constructor' would resolve to a function.
export const productOptionsFor = (industry?: string | null): ProductReadinessOption[] =>
  isIndustrySlug(industry) ? PRODUCT_READINESS_CATALOGUE[industry].options : NO_OPTIONS;

/**
 * The suffix a card metric key carries over the catalogue option id it reports —
 * `credit_card_limit_upgrade` on the catalogue, `credit_card_limit_upgrade_readiness` on the card.
 *
 * One join, declared once: `ProductReadinessSection` builds the dropdown from it,
 * `productReadiness/registry.ts` resolves a layout from it, and the registry test asserts every
 * product metric key the card declares resolves back to a catalogue option through it. PLA-8828.
 */
export const PRODUCT_METRIC_SUFFIX = '_readiness';

/** The card metric key that carries this product's reading. */
export const productMetricKeyOf = (option: ProductReadinessOption): string =>
  `${option.id}${PRODUCT_METRIC_SUFFIX}`;

/** The card metric keys an industry's products report through, in catalogue order. */
export const productMetricKeysFor = (industry?: string | null): string[] =>
  productOptionsFor(industry).map(productMetricKeyOf);

/**
 * Every product metric key the catalogue names, across all four industries. Enumerated rather than
 * matched on the `_readiness` suffix: the card carries context tiles whose keys are not products,
 * and a suffix test would start dropping one the day a catalogue product is named without it.
 */
const ALL_PRODUCT_METRIC_KEYS = new Set<string>(
  Object.values(PRODUCT_READINESS_CATALOGUE).flatMap(({ options }) =>
    options.map(productMetricKeyOf)
  )
);

/** True for a key that carries SOME industry's product reading — never a context tile. */
export const isProductMetricKey = (key: string): boolean => ALL_PRODUCT_METRIC_KEYS.has(key);

/** The catalogue product id a product tile reports for, or `undefined` for a context tile. */
export const productIdOfMetricKey = (key: string): string | undefined =>
  isProductMetricKey(key) ? key.slice(0, -PRODUCT_METRIC_SUFFIX.length) : undefined;

/**
 * The catalogue option a card metric key reports for, within one industry. The inverse of
 * `productMetricKeyOf`, so a key belonging to another industry's catalogue answers `undefined`.
 */
export const productOptionForMetricKey = (
  industry: string | null | undefined,
  metricKey?: string
): ProductReadinessOption | undefined =>
  metricKey === undefined
    ? undefined
    : productOptionsFor(industry).find(option => productMetricKeyOf(option) === metricKey);

/**
 * One catalogue option by id, across all four industries.
 *
 * Safe as a flat lookup for the same reason `PRODUCT_READINESS_METRIC_ID_BY_PRODUCT` is: the 36
 * product ids are globally unique, asserted in `registry/__tests__/metricIdentifiers.test.ts`. The
 * data seam needs it because a fetch outcome arrives carrying only the scope's product id, with no
 * tenant industry beside it. PLA-8815.
 */
export const productOptionById = (productId?: string): ProductReadinessOption | undefined =>
  productId === undefined
    ? undefined
    : Object.values(PRODUCT_READINESS_CATALOGUE)
        .flatMap(({ options }) => options)
        .find(option => option.id === productId);

/**
 * The wire `product_family` values a product's rows are selected by, or `undefined` where the
 * catalogue names none — which is NOT the same as an empty list, and the two must stay apart: no
 * declared family means "this side cannot tell which rows are this product's", while an empty one
 * would mean "none of them are".
 */
export const wireProductFamiliesFor = (productId?: string): string[] | undefined =>
  productOptionById(productId)?.wireProductFamilies;

export const recommendedProductFor = (industry?: string | null): ProductReadinessOption | null => {
  if (!isIndustrySlug(industry)) {
    return null;
  }

  const { options, recommendedDefaultId } = PRODUCT_READINESS_CATALOGUE[industry];

  return options.find(option => option.id === recommendedDefaultId) ?? null;
};

/* ---------------------------------------------------------------------------------------------
   The holding-branch switch.

   Kept beside the option catalogue rather than inside a layout because it is CATALOGUE knowledge,
   not drawing knowledge: which field answers the question, what the score is called in each branch,
   and which fields stop applying. A layout consumes the answer; it never decides it. PLA-8828.
   --------------------------------------------------------------------------------------------- */

/**
 * The words the holding field answers with. Compared case-insensitively and trimmed only.
 *
 * `holder` / `non_holder` are what `target_product_holding_status` actually carries on the landed
 * `O-*-PR-*` rows; without them every live row resolved to `unknown` and both branches of the card
 * stayed empty over real counts. PLA-8815.
 */
const HELD_ANSWERS = new Set(['yes', 'y', 'true', 'held', 'holder']);
const NOT_HELD_ANSWERS = new Set([
  'no',
  'n',
  'false',
  'not held',
  'none',
  'non_holder',
  'non-holder',
  'not_holder'
]);

/**
 * One holding answer as the branch it names, or `unknown` for anything the vocabulary does not
 * state. Exported because the wire's own column and the catalogue's per-product field have to agree
 * on what a word means — `data/productReadinessResult.ts` reads the row through this. PLA-8815.
 */
export const holdingStatusFromAnswer = (
  answer: string | null | undefined
): ProductHoldingStatus => {
  if (typeof answer !== 'string') {
    return 'unknown';
  }

  const normalised = answer.trim().toLowerCase();

  if (HELD_ANSWERS.has(normalised)) {
    return 'held';
  }

  return NOT_HELD_ANSWERS.has(normalised) ? 'not_held' : 'unknown';
};

/** The shape this module needs off a metric — `CardMetric`, minus everything it does not read. */
export interface HoldingStatusSource {
  key: string;
  valueText?: string;
}

/**
 * Every field the catalogue reads a holding answer out of, across all four industries.
 *
 * These are CONDITIONS, not tiles: the card declares them so the fixture and the live payload agree
 * on what it carries, and the body filters them out of the readings row from here rather than from a
 * hand-kept second list. PLA-8828.
 */
const HOLDING_STATUS_KEYS = new Set<string>([
  TARGET_PRODUCT_HOLDING_STATUS_KEY,
  ...Object.values(PRODUCT_READINESS_CATALOGUE).flatMap(({ options }) =>
    options
      .map(option => option.holding?.statusKey)
      .filter((key): key is string => key !== undefined)
  )
]);

export const isHoldingStatusKey = (key: string): boolean => HOLDING_STATUS_KEYS.has(key);

/**
 * Resolves `held` / `not_held` / `unknown` for a product from the payload's own holding field.
 *
 * Reads the product's own field first and the generic `target_product_holding_status` second, so a
 * product that names no field still branches. Anything the field does not answer with stays
 * `unknown` rather than collapsing to `not_held`.
 */
export const holdingStatusOf = (
  option: ProductReadinessOption | undefined,
  metrics: HoldingStatusSource[]
): ProductHoldingStatus => {
  if (!option) {
    return 'unknown';
  }

  const keys = [option.holding?.statusKey, TARGET_PRODUCT_HOLDING_STATUS_KEY].filter(
    (key): key is string => Boolean(key)
  );
  const answer = keys
    .map(key => metrics.find(metric => metric.key === key)?.valueText)
    .find(text => text !== undefined && text !== '');

  return holdingStatusFromAnswer(answer);
};

/**
 * What the product's readiness reading is CALLED in the resolved branch, or `undefined` where the
 * catalogue states no per-branch wording — in which case the caller keeps the metric's own label.
 *
 * Undefined rather than the option label on purpose: only Credit Card is renamed by the catalogue
 * (`Acquisition` vs `Limit Upgrade` readiness), and returning `option.label` for the rest would
 * silently retitle eight tiles the catalogue never spoke about. `unknown` renames nothing either —
 * the branch has not been established, so neither question has been asked yet.
 */
export const readinessLabelFor = (
  option: ProductReadinessOption | undefined,
  status: ProductHoldingStatus
): string | undefined =>
  status === 'unknown' ? undefined : option?.holding?.readinessLabel?.[status];

/**
 * The metric keys that stop applying in the resolved branch. Empty for a holder and for an
 * unconfirmed customer — only a CONFIRMED non-holder suppresses a field.
 */
export const notApplicableKeysFor = (
  option: ProductReadinessOption | undefined,
  status: ProductHoldingStatus
): string[] => (status === 'not_held' ? option?.holding?.notApplicableWhenNotHeld ?? [] : []);

export default PRODUCT_READINESS_CATALOGUE;
